import {
  createRecordingProviderRegistry,
  getLegacyRecordingIdentifiers,
  RECORDING_PROVIDERS,
} from 'src/live'

const recordingProviderRegistry = createRecordingProviderRegistry()

function resolveRecordingProvider(recordingProvider) {
  return (
    recordingProviderRegistry.get(recordingProvider) ||
    recordingProviderRegistry.get(RECORDING_PROVIDERS.agoraCloud)
  )
}

function createRecordingServiceResult(providerId, result, fallbackError) {
  const recordingSession = result?.recordingSession || null
  const legacyIdentifiers = getLegacyRecordingIdentifiers(recordingSession)
  const success = result?.success === true

  return {
    success,
    error: success ? null : result?.error || fallbackError,
    provider: providerId,
    status: result?.status || null,
    recordingSession,
    rawData: result?.rawData || null,
    ...legacyIdentifiers,
  }
}

export const recordingService = {
  // Start recording session
  async startRecording(bookedEventId, uidRecording, { recordingProvider } = {}) {
    const provider = resolveRecordingProvider(recordingProvider)

    try {
      const result = await provider.start({
        bookedEventId,
        uidRecording,
      })

      return createRecordingServiceResult(provider.id, result, 'Failed to start recording')
    } catch (error) {
      console.error('Error starting recording:', error)
      return { success: false, error: error.message }
    }
  },

  // Stop recording session
  async stopRecording(
    bookedEventId,
    uidRecording,
    resourceId,
    sid,
    { recordingProvider } = {},
  ) {
    const provider = resolveRecordingProvider(recordingProvider)

    try {
      const result = await provider.stop({
        bookedEventId,
        uidRecording,
        resourceId,
        sid,
      })

      return createRecordingServiceResult(provider.id, result, 'Failed to stop recording')
    } catch (error) {
      console.error('Error stopping recording:', error)
      return { success: false, error: error.message }
    }
  },

  // Query recording status
  async queryRecordingStatus(
    bookedEventId,
    resourceId,
    sid,
    { uidRecording, recordingProvider } = {},
  ) {
    const provider = resolveRecordingProvider(recordingProvider)

    try {
      const result = await provider.query({
        bookedEventId,
        uidRecording,
        resourceId,
        sid,
      })

      return createRecordingServiceResult(provider.id, result, 'Failed to query recording status')
    } catch (error) {
      console.error('Error querying recording status:', error)
      return { success: false, error: error.message }
    }
  },

  // Handle recording button click with full logic
  async handleRecordingToggle({
    isCurrentlyRecording,
    bookedEventId,
    uidRecording,
    recordingProvider,
    resourceId = null,
    sid = null,
    onRecordingStart,
    onRecordingStop,
    onError,
  }) {
    try {
      if (!isCurrentlyRecording) {
        // Start recording
        const startResult = await this.startRecording(bookedEventId, uidRecording, {
          recordingProvider,
        })

        if (startResult.success) {
          // Query status to confirm
          const statusResult = await this.queryRecordingStatus(bookedEventId, startResult.resourceId, startResult.sid, {
            uidRecording,
            recordingProvider,
          })

          if (statusResult.success) {
            onRecordingStart?.({
              resourceId: startResult.resourceId,
              sid: startResult.sid,
              recordingSession: startResult.recordingSession,
            })
            return { success: true, recording: true }
          } else {
            onError?.('Failed to confirm recording start')
            return { success: false }
          }
        } else {
          onError?.(startResult.error)
          return { success: false }
        }
      } else {
        // Stop recording
        const stopResult = await this.stopRecording(
          bookedEventId,
          uidRecording,
          resourceId,
          sid,
          { recordingProvider },
        )

        if (stopResult.success) {
          onRecordingStop?.(stopResult.recordingSession)
          return { success: true, recording: false }
        } else {
          onError?.(stopResult.error)
          return { success: false }
        }
      }
    } catch (error) {
      console.error('Error handling recording toggle:', error)
      onError?.(error.message)
      return { success: false }
    }
  },
}
